import type { User } from "@supabase/supabase-js";

/**
 * How close "confirmed" and "last signed in" must be for a session to count as
 * the account's first. The email-confirm path is the slow one: the address is
 * confirmed when the link is opened, and the session lands a few seconds later
 * when the callback route exchanges the code.
 */
const FIRST_SESSION_WINDOW_MS = 120_000;

type AuthTimestamps = Pick<
  User,
  "created_at" | "confirmed_at" | "email_confirmed_at" | "last_sign_in_at"
>;

/**
 * True for the first session of a new account — a sign-up, on every path:
 *  - email + confirmation link: `email_confirmed_at` is set on verify,
 *    `last_sign_in_at` at the code exchange seconds later;
 *  - email with autoconfirm, Google OAuth, Google One Tap: both come from the
 *    moment the user row is created.
 *
 * An existing email account that signs in with Google for the first time (the
 * identity is linked) keeps its old `email_confirmed_at`, so it reads false.
 *
 * `last_sign_in_at` does not move on a token refresh, so this stays true for
 * every reload of that first session — the caller de-duplicates
 * (`captureSignUp`). Needs the FULL session user: the minimal `{id, email}`
 * built from `getClaims()` has none of these fields and reads false.
 */
export function isFirstSession(user: Partial<AuthTimestamps> | null | undefined): boolean {
  if (!user) return false;
  const first = Date.parse(user.email_confirmed_at ?? user.confirmed_at ?? user.created_at ?? "");
  const last = Date.parse(user.last_sign_in_at ?? "");
  return (
    Number.isFinite(first) &&
    Number.isFinite(last) &&
    Math.abs(last - first) < FIRST_SESSION_WINDOW_MS
  );
}
